import { useRef, useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Paper, List, ListItem, ListItemButton, ListItemText, CircularProgress, Checkbox, ClickAwayListener } from '@mui/material';
import { Button, Input, Text } from '@fragments';
import { useTopModalZIndex } from '@hooks/useModalZIndex';
import './styles.scss';
import { SearchRounded } from '@mui/icons-material';
import { IconCheck } from '@tabler/icons-react';



// Normalizes a string for comparison: lowercase, no accents, no special chars
function normalizeStr(str) {
    return String(str ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .trim();
}

export default function Popper({ controlRef, children, config = {}, paperSx = {}, onOpenChange }) {
    // `titleHeader`: the header is a group label (an eyebrow) over the options, not a
    // panel of its own: aligned with the option text, no rule, close to the first row.
    // `fitContent`: the popper takes its content's width (up to the Paper's maxWidth)
    // instead of the 248px column, so long labels stay on one line.
    // `fitHeight`: the list takes its content's height (up to the viewport) instead of
    // scrolling past 320px, so a long action menu shows whole.
    const { multiple = false, searchKeyExtractor = null, disableFlip = false, activateOnHover = false, titleHeader = false, fitContent = false, fitHeight = false } = config;
    const { t } = useTranslation('fragments');

    const hasFooter = multiple || !!searchKeyExtractor;

    const topModalZIndex = useTopModalZIndex();

    const [open, setOpen] = useState(false);
    const [loadingItemId, setLoadingItemId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [popperEl, setPopperEl] = useState(null);
    const [options, setOptions] = useState([]);
    const [header, setHeader] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (onOpenChange) onOpenChange(open);
    }, [open, onOpenChange]);

    // Internal pending selection state (only used when multiple=true)
    const [pendingChecked, setPendingChecked] = useState(new Set());
    const initialCheckedRef = useRef(new Set());

    const sideRef = useRef('bottom');

    const handleClose = useCallback(() => {
        setOpen(false);
        setLoadingItemId(null);
        setAnchorEl(null);
        setSearchQuery('');
        setHeader(null);
    }, []);

    // Fires pending onChange callbacks for every option whose state changed, then closes
    const handleConfirm = useCallback((currentOptions, currentPending) => {
        if (multiple) {
            const initial = initialCheckedRef.current;
            currentOptions.forEach(opt => {
                const wasChecked = initial.has(opt.id);
                const isNowChecked = currentPending.has(opt.id);
                if (wasChecked !== isNowChecked && opt.onClick) {
                    opt.onClick();
                }
            });
        }
        handleClose();
    }, [multiple, handleClose]);

    // ClickAway: confirm if multiple, otherwise just close
    const handleClickAway = useCallback(() => {
        if (multiple) {
            handleConfirm(options, pendingChecked);
        } else {
            handleClose();
        }
    }, [multiple, options, pendingChecked, handleConfirm, handleClose]);

    // --- Hover-activated close (config.activateOnHover) ---
    // When enabled, the popper behaves as a hover menu: it stays open while the
    // pointer is over the anchor OR the popper, and closes shortly after the
    // pointer leaves both. The grace delay bridges the 8px gap between anchor and
    // popper, so moving the cursor across it doesn't dismiss the menu.
    // The consumer is responsible for opening it on the anchor's onMouseEnter.
    const hoverCloseTimerRef = useRef(null);
    const closeBehaviorRef = useRef(handleClickAway);
    closeBehaviorRef.current = handleClickAway;

    const cancelHoverClose = useCallback(() => {
        if (hoverCloseTimerRef.current) {
            clearTimeout(hoverCloseTimerRef.current);
            hoverCloseTimerRef.current = null;
        }
    }, []);

    const scheduleHoverClose = useCallback(() => {
        cancelHoverClose();
        hoverCloseTimerRef.current = setTimeout(() => {
            hoverCloseTimerRef.current = null;
            closeBehaviorRef.current?.();
        }, 160);
    }, [cancelHoverClose]);

    // Expose controlRef API
    useEffect(() => {
        if (!controlRef) return;

        controlRef.current = {
            /**
             * Opens the popper with the given options.
             * @param {Array} opts - [{id, label, icon, variant, disabled, onClick, checked?, active?, divider?, right?, noHover?}]
             *   active: marks the option as the current selection — renders a trailing check icon.
             * @param {string} side - placement (default: 'bottom')
             * @param {Element} anchorElement - anchor element for positioning
             * @param {ReactNode} headerContent - optional header content
             */
            open(opts, side = 'bottom', anchorElement, headerContent = null) {
                sideRef.current = side;
                const safeOpts = opts || [];

                setOptions(safeOpts);
                setAnchorEl(anchorElement);
                setHeader(headerContent);
                setSearchQuery('');

                if (multiple) {
                    const initialSet = new Set(
                        safeOpts.filter(o => o.checked).map(o => o.id)
                    );
                    initialCheckedRef.current = initialSet;
                    setPendingChecked(new Set(initialSet));
                }

                setOpen(true);
            },

            close() {
                handleClose();
            },
        };
    }, [controlRef, multiple, handleClose]);

    // While open with activateOnHover, keep the menu alive as long as the pointer
    // is over the anchor; arm the close timer when it leaves (the popper's own
    // mouseenter cancels it when the cursor lands on the menu). A fast move that
    // jumps straight from the anchor onto the menu fires the popper's (React)
    // mouseenter before this native mouseleave, so that move must not arm it.
    useEffect(() => {
        if (!open || !activateOnHover || !anchorEl) return undefined;

        const onLeave = (event) => {
            if (popperEl && event.relatedTarget instanceof Node && popperEl.contains(event.relatedTarget)) return;
            scheduleHoverClose();
        };
        const onEnter = () => cancelHoverClose();
        anchorEl.addEventListener('mouseleave', onLeave);
        anchorEl.addEventListener('mouseenter', onEnter);

        return () => {
            anchorEl.removeEventListener('mouseleave', onLeave);
            anchorEl.removeEventListener('mouseenter', onEnter);
            cancelHoverClose();
        };
    }, [open, activateOnHover, anchorEl, popperEl, scheduleHoverClose, cancelHoverClose]);

    // Filter options when searchKeyExtractor is set
    const filteredOptions = useMemo(() => {
        if (!searchKeyExtractor || !searchQuery.trim()) return options;

        const query = normalizeStr(searchQuery);
        const validate = (value) => normalizeStr(value).includes(query);

        return options.filter(opt => searchKeyExtractor(opt, validate));
    }, [options, searchQuery, searchKeyExtractor]);

    // Handle option click
    const handleOptionClick = async (option) => {
        if (option.disabled || option.noHover) return;

        if (multiple) {
            // Only update internal visual state — don't call onClick yet
            setPendingChecked(prev => {
                const next = new Set(prev);
                if (next.has(option.id)) next.delete(option.id);
                else next.add(option.id);
                return next;
            });
            return;
        }

        if (loadingItemId !== null || !option.onClick) return;

        try {
            setLoadingItemId(option.id);
            await option.onClick();
            handleClose();
        } catch (error) {
            console.error('Erro ao executar onClick:', error);
        } finally {
            setLoadingItemId(null);
        }
    };

    const renderIcon = (option) => {
        if (multiple) {
            const isChecked = pendingChecked.has(option.id);
            return (
                <span style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'center', minWidth: 20, marginLeft: '4px', flexShrink: 0 }}>
                    <Checkbox
                        checked={isChecked}
                        size="small"
                        disableRipple
                        sx={{
                            p: 0,
                            color: 'rgba(255,255,255,0.3)',
                            '&.Mui-checked': { color: 'var(--primary-6)' },
                        }}
                    />
                </span>
            );
        }

        if (option.icon) {
            return (
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignSelf: 'center',
                    minWidth: 28,
                    flexShrink: 0,
                    color: 'inherit',
                    lineHeight: 0,
                }}>
                    {option.icon}
                </span>
            );
        }

        return null;
    };

    const renderLoader = (option) => {
        if (!multiple && loadingItemId === option.id) {
            return (
                <CircularProgress size={16} sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
            );
        }
        return null;
    };

    // Portal target: a div appended directly to <body>, so the popper escapes any
    // ancestor `overflow`/`transform`/`clip-path` (e.g. the modal's centered Box).
    const portalNodeRef = useRef(null);
    if (typeof document !== 'undefined' && portalNodeRef.current === null) {
        portalNodeRef.current = document.createElement('div');
        portalNodeRef.current.className = 'custom-popper-portal';
    }
    useEffect(() => {
        const node = portalNodeRef.current;
        if (!node || typeof document === 'undefined') return undefined;
        document.body.appendChild(node);
        return () => {
            if (node.parentNode) node.parentNode.removeChild(node);
        };
    }, []);

    // Position the popper manually via getBoundingClientRect. Avoids any timing /
    // strategy quirks of Popper.js wrappers. Recomputes on open, on anchor change,
    // and on scroll/resize while open.
    const [position, setPosition] = useState({ top: 0, left: 0, ready: false });
    const placement = sideRef.current;

    useLayoutEffect(() => {
        if (!open || !anchorEl || !popperEl) {
            setPosition((prev) => ({ ...prev, ready: false }));
            return undefined;
        }

        function compute() {
            const anchorRect = anchorEl.getBoundingClientRect();
            const popperRect = popperEl.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const gap = 8;
            const margin = 8;

            const [base, align] = String(placement || 'bottom').split('-');

            let top;
            let left;

            if (base === 'top') {
                top = anchorRect.top - popperRect.height - gap;
            } else if (base === 'left') {
                top = anchorRect.top;
                left = anchorRect.left - popperRect.width - gap;
            } else if (base === 'right') {
                top = anchorRect.top;
                left = anchorRect.right + gap;
            } else { // bottom (default)
                top = anchorRect.bottom + gap;
            }

            if (base === 'top' || base === 'bottom') {
                if (align === 'end') {
                    left = anchorRect.right - popperRect.width;
                } else if (align === 'start') {
                    left = anchorRect.left;
                } else {
                    left = anchorRect.left + (anchorRect.width - popperRect.width) / 2;
                }
            } else if (base === 'left' || base === 'right') {
                if (align === 'end') {
                    top = anchorRect.bottom - popperRect.height;
                } else if (align === 'start') {
                    top = anchorRect.top;
                } else {
                    top = anchorRect.top + (anchorRect.height - popperRect.height) / 2;
                }
            }

            // Flip if not enough room (preserving alignment)
            if (!disableFlip) {
                if (base === 'bottom' && top + popperRect.height > vh - margin) {
                    const flippedTop = anchorRect.top - popperRect.height - gap;
                    if (flippedTop >= margin) top = flippedTop;
                } else if (base === 'top' && top < margin) {
                    const flippedTop = anchorRect.bottom + gap;
                    if (flippedTop + popperRect.height <= vh - margin) top = flippedTop;
                }
            }

            // Clamp into viewport
            left = Math.max(margin, Math.min(left, vw - popperRect.width - margin));
            top = Math.max(margin, Math.min(top, vh - popperRect.height - margin));

            setPosition({ top, left, ready: true });
        }

        compute();
        window.addEventListener('scroll', compute, true);
        window.addEventListener('resize', compute);
        return () => {
            window.removeEventListener('scroll', compute, true);
            window.removeEventListener('resize', compute);
        };
    }, [open, anchorEl, popperEl, placement, disableFlip]);

    const popperContent = (
        <div
            ref={setPopperEl}
            className="custom-popper"
            // The popper is portaled to <body>, but React synthetic events still
            // bubble through the React tree to the logical parent (e.g. a clickable
            // table row). Stop propagation here so option clicks never trigger the
            // anchor's ancestor handlers.
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={activateOnHover ? cancelHoverClose : undefined}
            onMouseLeave={activateOnHover ? scheduleHoverClose : undefined}
            style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                // Float just above the topmost modal (9999 when none is open),
                // so dropdowns stay usable inside a stacked modal like the
                // form builder. See useModalZIndex.
                zIndex: topModalZIndex,
                visibility: position.ready ? 'visible' : 'hidden',
                // Measured for placement, so it must be as wide as the Paper it holds.
                ...(fitContent ? { width: 'max-content', maxWidth: 'calc(100vw - 32px)' } : null),
            }}
        >
            <ClickAwayListener onClickAway={handleClickAway}>
                <Paper
                        elevation={0}
                        sx={{
                            backgroundColor: 'var(--surface-10)',
                            borderRadius: 'var(--radius-xl)',
                            border: '1px solid var(--surface-8)',
                            boxShadow: 'var(--shadow-overlay)',
                            overflow: 'hidden',
                            minWidth: 200,
                            maxWidth: 'min(300px, calc(100vw - 32px))',
                            width: fitContent ? 'max-content' : 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            ...paperSx,
                        }}
                    >
                        {/* Header */}
                        {header && (
                            <div className={`popper-header${titleHeader ? ' popper-header--title' : ''}`}>
                                {header}
                            </div>
                        )}

                        {/* Search field */}
                        {searchKeyExtractor && (
                            <div className="popper-search-bar">
                                <Input.Text
                                    end={<SearchRounded />}
                                    value={searchQuery}
                                    onInput={setSearchQuery}
                                    placeholder={t('popper.searchPlaceholder')}
                                    autoFocus
                                />
                            </div>
                        )}

                        {/* Options list */}
                        <List
                            sx={{
                                // Vertical breathing room only — horizontal inset lives on each
                                // item so dividers can still run edge to edge (touch the corners).
                                px: 0,
                                py: '8px',
                                maxHeight: fitHeight ? 'calc(100dvh - 32px)' : hasFooter ? '232px' : '320px',
                                overflowY: 'auto',
                                flexShrink: 1,
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            }}
                        >
                            {filteredOptions.length === 0 && searchQuery.trim() ? (
                                <ListItem sx={{ py: 2, justifyContent: 'center' }}>
                                    <Text.span
                                        children={t('popper.noResults')}
                                        style={{
                                            textAlign: 'center',
                                        }}
                                    />
                                </ListItem>
                            ) : (
                                filteredOptions.map((option, index) => {
                                    if (option.divider) {
                                        return <hr key={`divider-${index}`} className="popper-divider" />;
                                    }

                                    const isChecked = multiple && pendingChecked.has(option.id);

                                    return (
                                        <ListItem
                                            key={option.id ?? index}
                                            disablePadding
                                            // Horizontal inset: keeps the hover highlight tucked away
                                            // from the corners for a softer, more comfortable feel.
                                            sx={{ px: 'var(--gap-1)' }}
                                        >
                                            <ListItemButton
                                                onClick={() => handleOptionClick(option)}
                                                disabled={option.disabled || (!multiple && loadingItemId !== null)}
                                                className={`popper-option popper-option-${option.variant || 'default'}`}
                                                sx={{
                                                    py: 1.1,
                                                    px: 1.5,
                                                    // Concentric with the 16px Paper minus the 8px inset.
                                                    borderRadius: 'var(--gap-1)',
                                                    alignItems: 'center',
                                                    cursor: option.noHover ? 'default' : 'pointer',
                                                    color: option.disabled
                                                        ? 'var(--surface-5)'
                                                        : 'var(--surface-2)',
                                                    backgroundColor: isChecked
                                                        ? 'var(--primary-bg)'
                                                        : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: (option.disabled || option.noHover)
                                                            ? (isChecked ? 'var(--primary-bg)' : 'transparent')
                                                            : isChecked
                                                                ? 'var(--primary-bg)'
                                                                : 'var(--overlay-surface)',
                                                    },
                                                    '&.Mui-disabled': { opacity: 0.5 },
                                                }}
                                            >
                                                {renderIcon(option)}
                                                <ListItemText
                                                    primary={option.label}
                                                    sx={{
                                                        paddingLeft: (option.icon || multiple) ? '0px' : '8px',
                                                        '& .MuiListItemText-primary': {
                                                            fontSize: '14px',
                                                            fontWeight: (isChecked || option.active) ? 500 : 400,
                                                            // A long single word (an @, a URL) wraps instead of
                                                            // running under the trailing content.
                                                            overflowWrap: 'anywhere',
                                                        },
                                                    }}
                                                />
                                                {option.right}
                                                {option.active && (
                                                    <span style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        alignSelf: 'center',
                                                        marginLeft: 8,
                                                        flexShrink: 0,
                                                        color: 'var(--primary-6)',
                                                        lineHeight: 0,
                                                    }}>
                                                        <IconCheck size={16} stroke={2.5} />
                                                    </span>
                                                )}
                                                {renderLoader(option)}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })
                            )}
                        </List>

                        {/* Footer: Aplicar button */}
                        {hasFooter && (
                            <div className="popper-footer">
                                <Button
                                    text={t('popper.apply')}
                                    size="small"
                                    color="inverse"
                                    variant="rounded"
                                    fullWidth
                                    onClick={() => handleConfirm(options, pendingChecked)}
                                />
                            </div>
                        )}
                </Paper>
            </ClickAwayListener>
        </div>
    );

    return (
        <>
            {children}
            {open && anchorEl && portalNodeRef.current
                ? createPortal(popperContent, portalNodeRef.current)
                : null}
        </>
    );
}
